import { useState, Fragment, useMemo, useRef, useCallback } from "react";
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  AlertTriangle,
  ArrowUpDown,
} from "@credcore/core-ui/icons";
import { cn } from "@/lib/utils";
import { formatNumber } from "@/lib/utils";
import { ErrorBreakdownMini } from "./ErrorBreakdownMini";
import {
  AnalystPerformance,
  ReviewerPerformance,
} from "@/graphql/types/models";
import { getScoreColor, ERROR_COLORS } from "@/lib/scoring";
import {
  ClassificationType,
  ClassificationWeight,
} from "@/graphql/types/models";
import { useVirtualizer } from "@tanstack/react-virtual";

type PerformanceNode = AnalystPerformance | ReviewerPerformance;

type SortField =
  | "name"
  | "documents"
  | "fields"
  | "datapoints"
  | "errors"
  | "score";
type SortDirection = "asc" | "desc";

const isReviewer = (node: PerformanceNode): node is ReviewerPerformance =>
  node.__typename === "ReviewerPerformance";

const isAnalyst = (node: PerformanceNode): node is AnalystPerformance =>
  node.__typename === "AnalystPerformance";

interface PerformanceTableProps {
  data: PerformanceNode[];
  role?: "ANALYST" | "REVIEWER";
  loading?: boolean;
  dateRange?: { from?: Date; to?: Date };
  classificationWeights?: ClassificationWeight[];
}

export function PerformanceTable({
  data,
  role = "ANALYST",
  loading,
  dateRange,
  classificationWeights = [],
}: PerformanceTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>("score");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const dataWithScores = useMemo(() => {
    return data.map((node) => ({
      ...node,
      weightedScore: node.performancePercent,
    }));
  }, [data]);

  const sortedData = useMemo(() => {
    const result = [...dataWithScores];

    result.sort((a, b) => {
      let aValue: number | string;
      let bValue: number | string;

      switch (sortField) {
        case "name":
          aValue = a.personName.toLowerCase();
          bValue = b.personName.toLowerCase();
          break;
        case "documents":
          aValue = a.totalDocuments;
          bValue = b.totalDocuments;
          break;
        case "fields":
          aValue = a.totalFields;
          bValue = b.totalFields;
          break;
        case "datapoints":
          aValue = a.totalDatapoints;
          bValue = b.totalDatapoints;
          break;
        case "errors":
          aValue = a.totalErrors;
          bValue = b.totalErrors;
          break;
        case "score":
          aValue = a.weightedScore;
          bValue = b.weightedScore;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return result;
  }, [dataWithScores, sortField, sortDirection]);

  const rowVirtualizer = useVirtualizer({
    count: sortedData.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: useCallback(
      (index: number) => {
        const node = sortedData[index];
        return expandedRows.has(node.personId) ? 200 : 56;
      },
      [sortedData, expandedRows],
    ),
    overscan: 5,
  });

  const shouldVirtualize = sortedData.length > 50;

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3 text-gray-400" />;
    }
    return sortDirection === "asc" ? (
      <ChevronUp className="ml-1 h-3 w-3 text-gray-700" />
    ) : (
      <ChevronDown className="ml-1 h-3 w-3 text-gray-700" />
    );
  };

  const toggleRowExpansion = (personId: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(personId)) {
      newExpanded.delete(personId);
    } else {
      newExpanded.add(personId);
    }
    setExpandedRows(newExpanded);
  };

  if (loading) {
    return (
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="animate-pulse">
          <div className="h-12 border-b bg-gray-100"></div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-14 border-b bg-gray-50"></div>
          ))}
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="rounded-lg bg-white shadow">
        <div className="p-8 text-center text-gray-500">
          No performance data available.
        </div>
      </div>
    );
  }

  const renderRow = (node: (typeof sortedData)[number]) => {
    const isExpanded = expandedRows.has(node.personId);

    return (
      <Fragment key={node.personId}>
        <tr
          className="cursor-pointer hover:bg-gray-50"
          onClick={() => toggleRowExpansion(node.personId)}
        >
          <td className="px-2 py-3">
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )}
          </td>
          <td className="px-4 py-3">
            <div className="font-medium text-gray-900">{node.personName}</div>
            {node.personEmail && (
              <div className="text-xs text-gray-500">{node.personEmail}</div>
            )}
          </td>
          <td className="px-4 py-3 text-right text-gray-700">
            {formatNumber(node.totalDocuments)}
          </td>
          <td className="px-4 py-3 text-right text-gray-700">
            {formatNumber(node.totalFields)}
          </td>
          <td className="px-4 py-3 text-right text-gray-700">
            {formatNumber(node.totalDatapoints)}
          </td>
          <td className="px-4 py-3 text-right">
            {node.totalErrors > 0 ? (
              <span className="text-red-600">{node.totalErrors}</span>
            ) : (
              <span className="text-gray-400">0</span>
            )}
          </td>
          <td className="px-4 py-3 text-center">
            <span
              className={cn(
                "text-sm font-medium",
                getScoreColor(node.weightedScore),
              )}
            >
              {node.weightedScore.toFixed(1)}%
            </span>
          </td>
        </tr>

        {isExpanded && (
          <tr>
            <td colSpan={7} className="bg-gray-50 px-6 py-4">
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-gray-500" />
                  <h4 className="text-sm font-medium text-gray-900">
                    Error Breakdown
                  </h4>
                </div>

                {node.errorBreakdown && node.errorBreakdown.length > 0 ? (
                  <ErrorBreakdownMini
                    errors={node.errorBreakdown}
                    personId={node.personId}
                    role={role}
                    dateRange={dateRange}
                  />
                ) : (
                  <p className="text-sm text-gray-500">No errors to display</p>
                )}

                <div className="flex gap-6 border-t pt-3 text-sm">
                  <div>
                    <span className="text-gray-500">Days: </span>
                    <span className="font-medium">
                      {isAnalyst(node)
                        ? node.workDays
                        : isReviewer(node)
                          ? node.reviewDays
                          : "-"}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Documents: </span>
                    <span className="font-medium">
                      {formatNumber(node.totalDocuments)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Fields: </span>
                    <span className="font-medium">
                      {formatNumber(node.totalFields)}
                    </span>
                  </div>
                </div>
              </div>
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  const tableHeader = (
    <thead className="bg-gray-50 text-xs">
      <tr>
        <th className="w-10 px-2 py-3"></th>
        <th
          className="cursor-pointer px-4 py-3 text-left font-medium uppercase text-gray-500 hover:bg-gray-100"
          onClick={() => handleSort("name")}
        >
          <div className="flex items-center">
            Name
            <SortIcon field="name" />
          </div>
        </th>
        <th
          className="cursor-pointer px-4 py-3 text-right font-medium uppercase text-gray-500 hover:bg-gray-100"
          onClick={() => handleSort("documents")}
        >
          <div className="flex items-center justify-end">
            Documents
            <SortIcon field="documents" />
          </div>
        </th>
        <th
          className="cursor-pointer px-4 py-3 text-right font-medium uppercase text-gray-500 hover:bg-gray-100"
          onClick={() => handleSort("fields")}
        >
          <div className="flex items-center justify-end">
            Fields
            <SortIcon field="fields" />
          </div>
        </th>
        <th
          className="cursor-pointer px-4 py-3 text-right font-medium uppercase text-gray-500 hover:bg-gray-100"
          onClick={() => handleSort("datapoints")}
        >
          <div className="flex items-center justify-end">
            Datapoints
            <SortIcon field="datapoints" />
          </div>
        </th>
        <th
          className="cursor-pointer px-4 py-3 text-right font-medium uppercase text-gray-500 hover:bg-gray-100"
          onClick={() => handleSort("errors")}
        >
          <div className="flex items-center justify-end">
            Errors
            <SortIcon field="errors" />
          </div>
        </th>
        <th
          className="cursor-pointer px-4 py-3 text-center font-medium uppercase text-gray-500 hover:bg-gray-100"
          onClick={() => handleSort("score")}
        >
          <div className="flex items-center justify-center">
            Score
            <SortIcon field="score" />
          </div>
        </th>
      </tr>
    </thead>
  );

  return (
    <div className="overflow-hidden rounded-lg bg-white shadow">
      {classificationWeights.length > 0 && (
        <div className="flex flex-wrap items-center gap-4 border-b bg-gray-50 px-4 py-3">
          <span className="text-xs font-medium uppercase text-gray-500">
            Score Weights:
          </span>
          {classificationWeights.map((cw) => {
            const colors =
              ERROR_COLORS[cw.classification] ||
              ERROR_COLORS[ClassificationType.INCORRECT_EXTRACTION];
            return (
              <div
                key={cw.classification}
                className="flex items-center gap-1.5"
              >
                <div className={cn("h-2.5 w-2.5 rounded-full", colors.bar)} />
                <span className="text-xs text-gray-600">
                  {cw.label}{" "}
                  <span className="text-gray-400">
                    ({(cw.weight * 100).toFixed(0)}%)
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {shouldVirtualize ? (
        <div ref={tableContainerRef} className="max-h-[600px] overflow-auto">
          <table className="w-full">
            {tableHeader}
            <tbody className="divide-y divide-gray-100 text-sm">
              <tr style={{ height: rowVirtualizer.getTotalSize() }}>
                <td colSpan={7} className="relative p-0">
                  {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                    const node = sortedData[virtualRow.index];
                    return (
                      <div
                        key={node.personId}
                        className="absolute left-0 right-0"
                        style={{
                          transform: `translateY(${virtualRow.start}px)`,
                        }}
                      >
                        <table className="w-full">
                          <tbody className="divide-y divide-gray-100 text-sm">
                            {renderRow(node)}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            {tableHeader}
            <tbody className="divide-y divide-gray-100 text-sm">
              {sortedData.map(renderRow)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
